import type { HTMLReactParserOptions } from "html-react-parser";
import parse from "html-react-parser";

import type { DeckFormatEnum, Option, Prisma, PrismaPromise } from "@sorcery/db";
import { DeckVisibilityEnum, ElementEnum, FilterTypeEnum } from "@sorcery/db";
import {
  escapeQueryForRelevanceScoring,
  escapeQueryForSearching,
} from "@sorcery/utils/server/search/full-text";

import { TrackedDeckChangeBoardEnum } from "@sorcery/db";
import dayjs from "dayjs";
import type { prisma as curiosaClientPrisma } from "~/server/db/client";
import { DECK_SORT_DEFAULT, DeckDividerEnum, DeckSortEnum } from "~/types/deck";
import { SEARCH_SORT } from "./filters";

const DECK_SORT_TIEBREAKER_ORDERBY_STATEMENT = {
  updatedAt: "desc",
} satisfies Prisma.DeckOrderByWithRelationInput;

export const isPrivate = (visibility: DeckVisibilityEnum) => {
  return visibility === DeckVisibilityEnum.Private;
};

export const hasPrimer = (html?: string | null) => {
  if (!html) return false;
  let hasContent = false;
  const getHtmlToReactParserOptions = (): HTMLReactParserOptions => {
    return {
      replace: (domNode) => {
        if ((domNode.type as string) === "text") {
          hasContent = true;
        }
      },
    };
  };
  const parserOptions = getHtmlToReactParserOptions();
  parse(html, parserOptions);
  return hasContent;
};

export const getDeckWhere = (
  // override option value as we know it will always be a string during api filtering
  filters: (Option & { value: string })[],
  avatar: string, // * for all, else card's id
  divider: DeckDividerEnum,
  following?: { id: string }[] | null,
  query?: string | null,
  scopingEmail?: string | null,
  visibility?: DeckVisibilityEnum | "*",
  folderId?: string | null,
) => {
  const where = {
    AND: [] as Prisma.DeckWhereInput[],
  };

  // Filter on visibility
  if (visibility !== "*") {
    where.AND.push({
      visibility: visibility,
    });
  }

  // If undefined, wont filter on folder. If null, will show root level. If value, show decks in target folder
  where.AND.push({
    folderId,
  });

  // Filter on divider & scope
  const scope = scopingEmail ? { email: scopingEmail } : null;
  if (divider !== DeckDividerEnum.ALL && !!scope) {
    where.AND.push(
      // null search option indicates its a target user's decks
      divider === DeckDividerEnum.MINE
        ? {
            user: scope,
          }
        : divider === DeckDividerEnum.FOLLOWING && !!following
          ? {
              user: {
                id: {
                  in: following.map((f) => f.id),
                },
              },
            }
          : // likes
            {
              likes: {
                some: {
                  user: scope,
                },
              },
            },
    );
  }

  // Filter on avatars
  if (avatar !== "*") {
    where.AND.push({
      avatars: {
        some: {
          cardId: avatar,
        },
      },
    });
  }

  // Add search query
  if (query) {
    const sanitisedQuery = escapeQueryForSearching(query);
    where.AND.push({
      name: { contains: sanitisedQuery },
    });
  }

  // Add filters
  if (filters.length) {
    // format filters
    const formatFilters = filters.filter((f) => f.type === FilterTypeEnum.FORMAT);
    if (formatFilters.length) {
      where.AND.push({
        format: {
          in: formatFilters.map((f) => f.value as DeckFormatEnum),
        },
      });
    }

    // element filters
    const elementFilters = filters.filter((f) => f.type === FilterTypeEnum.ELEMENT);
    if (elementFilters.length) {
      const selectedElementIds = elementFilters.map((element) => element.value.toLowerCase());

      where.AND.push(
        {
          elements: {
            every: {
              id: {
                in: selectedElementIds.concat(ElementEnum.None.toLowerCase()),
              },
            },
          },
        },
        {
          elements: {
            some: {
              id: { in: selectedElementIds },
            },
          },
        },
      );
    }

    // inclusion filters
    const inclusionFilters = filters.filter((f) => f.type === FilterTypeEnum.INCLUSION);
    if (inclusionFilters.length) {
      // make sure there is a decklist
      where.AND.push({
        AND: [
          {
            decklist: {
              some: {},
            },
          },
          {
            avatars: {
              some: {},
            },
          },
        ],
      });
      // filter to relevant inclusions
      where.AND.push(
        ...inclusionFilters.map((i) => ({
          OR: [
            {
              decklist: {
                some: {
                  card: {
                    id: i.value,
                  },
                },
              },
            },
            {
              avatars: {
                some: {
                  card: {
                    id: i.value,
                  },
                },
              },
            },
          ],
        })),
      );
    }

    // exclusion filters
    const exclusionFilters = filters.filter((f) => f.type === FilterTypeEnum.EXCLUSION);
    if (exclusionFilters.length) {
      // make sure there is a decklist
      where.AND.push({
        AND: [
          {
            decklist: {
              some: {},
            },
          },
          {
            avatars: {
              some: {},
            },
          },
        ],
      });
      // filter to relevant exclusions
      where.AND.push({
        AND: [
          {
            decklist: {
              none: {
                card: {
                  id: {
                    in: exclusionFilters.map((f) => f.value),
                  },
                },
              },
            },
          },
          {
            avatars: {
              none: {
                card: {
                  id: {
                    in: exclusionFilters.map((f) => f.value),
                  },
                },
              },
            },
          },
        ],
      });
    }
  }
  return where;
};

export const getDeckOrderBy = (sort?: DeckSortEnum | typeof SEARCH_SORT, query?: string | null) => {
  // default to updatedAt ordering or relevance if there's a search term
  const defaultSort = query ? SEARCH_SORT : DECK_SORT_DEFAULT;
  const baseSort = getSort(sort ?? defaultSort, query);
  const tiebreaker = getSort(DECK_SORT_DEFAULT);

  if (baseSort[0] === tiebreaker[0]) {
    return baseSort satisfies Prisma.DeckOrderByWithRelationInput[];
  }
  return [...baseSort, ...tiebreaker] satisfies Prisma.DeckOrderByWithRelationInput[];
};

// helper for ordering
const getSort = (
  sort: DeckSortEnum | typeof SEARCH_SORT,
  query?: string | null,
): Prisma.DeckOrderByWithRelationInput[] => {
  switch (sort) {
    case SEARCH_SORT:
      return [
        {
          _relevance: {
            fields: "name",
            search: query ? escapeQueryForRelevanceScoring(query) : "",
            sort: "desc",
          },
        },
      ];
    case DeckSortEnum.LATEST:
      return [DECK_SORT_TIEBREAKER_ORDERBY_STATEMENT];
    case DeckSortEnum.NAME:
      return [{ name: "asc" }];
    case DeckSortEnum.ALLTIME:
      return [{ views: { _count: "desc" } }];
    case DeckSortEnum.TRENDING:
      return [{ hotscore: "desc" }];
    default:
      throw new Error(`Invalid sort type: ${sort as string}`);
  }
};

export const addDeckCard = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  cardId: string,
  variantId: string,
  quantity: number,
  elements: {
    id: string;
  }[],
  idOfCardAlreadyInDecklist?: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      decklist: {
        upsert: {
          where: {
            id: idOfCardAlreadyInDecklist ?? "dummy",
          },
          create: {
            quantity: quantity,
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
          update: {
            quantity: quantity,
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
        },
      },
      elements: {
        // needs testing, may create duplicate elements
        connect: elements.map((element) => ({
          id: element.id,
        })),
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const removeDeckCard = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  decklist: {
    id: string;
    card: {
      elements: {
        id: string;
      }[];
    };
  }[],
  idOfCardAlreadyInDecklist: string,
): PrismaPromise<{ id: string }> => {
  const resultingElements = Object.values(ElementEnum)
    .filter((e) =>
      decklist.find(
        (dli) =>
          dli.id !== idOfCardAlreadyInDecklist &&
          dli.card.elements.some((el) => el.id === e.toLowerCase()),
      ),
    )
    .map((e) => ({
      id: e.toLowerCase(),
    }));

  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      elements: {
        set: resultingElements,
      },
      decklist: {
        delete: {
          id: idOfCardAlreadyInDecklist,
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const addSideboardCard = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  cardId: string,
  variantId: string,
  quantity: number,
  idOfCardAlreadyInSideboard?: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      sideboard: {
        upsert: {
          where: {
            id: idOfCardAlreadyInSideboard ?? "dummy",
          },
          create: {
            quantity: quantity,
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
          update: {
            quantity: quantity,
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const removeSideboardCard = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  idOfCardAlreadyInSideboard: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      sideboard: {
        delete: {
          id: idOfCardAlreadyInSideboard,
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const addMaybeboardCard = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  cardId: string,
  variantId: string,
  quantity: number,
  idOfCardAlreadyInMaybeboard?: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      maybeboard: {
        upsert: {
          where: {
            id: idOfCardAlreadyInMaybeboard ?? "dummy",
          },
          create: {
            quantity: quantity,
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
          update: {
            quantity: quantity,
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const removeMaybeboardCard = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  idOfCardAlreadyInMaybeboard: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      maybeboard: {
        delete: {
          id: idOfCardAlreadyInMaybeboard,
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const updateAvatar = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  cardId: string,
  variantId: string,
  currentAvatarCardId: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      avatars: {
        update: {
          where: {
            id: currentAvatarCardId,
          },
          data: {
            card: {
              connect: {
                id: cardId,
              },
            },
            variant: {
              connect: {
                id: variantId,
              },
            },
          },
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const updateFeature = (
  prisma: typeof curiosaClientPrisma,
  updatedAt: string,
  deckId: string,
  variantId: string,
): PrismaPromise<{ id: string }> => {
  return prisma.deck.update({
    where: {
      id: deckId,
    },
    data: {
      updatedAt,
      feature: {
        connect: {
          id: variantId,
        },
      },
    },
    select: {
      // SELECT NONE
      // https://github.com/prisma/prisma/issues/6252
      id: true,
    },
  });
};

export const recordDeckChange = (
  prisma: typeof curiosaClientPrisma,
  deckId: string,
  cardId: string,
  variantId: string,
  quantity: number,
) => {
  return prisma.deckChange.create({
    data: {
      board: TrackedDeckChangeBoardEnum.Main,
      quantity,
      deck: {
        connect: {
          id: deckId,
        },
      },
      card: {
        connect: {
          id: cardId,
        },
      },
      variant: {
        connect: {
          id: variantId,
        },
      },
    },
  });
};

export const recordAvatarChange = (
  prisma: typeof curiosaClientPrisma,
  deckId: string,
  cardId: string,
  variantId: string,
  quantity: number,
) => {
  return prisma.deckChange.create({
    data: {
      board: TrackedDeckChangeBoardEnum.Avatar,
      quantity,
      deck: {
        connect: {
          id: deckId,
        },
      },
      card: {
        connect: {
          id: cardId,
        },
      },
      variant: {
        connect: {
          id: variantId,
        },
      },
    },
  });
};

export const getTimeRangeLabel = (timestamp: Date): string => {
  const now = dayjs();
  const timeDiff = now.diff(timestamp);

  const timeRanges = [
    { label: "Just now", ms: 60 * 60 * 1000 },
    { label: "A few hours ago", ms: 24 * 60 * 60 * 1000 },
    { label: "1 day ago", ms: 2 * 24 * 60 * 60 * 1000 },
    { label: "2 days ago", ms: 3 * 24 * 60 * 60 * 1000 },
    { label: "3 days ago", ms: 4 * 24 * 60 * 60 * 1000 },
    { label: "4 days ago", ms: 5 * 24 * 60 * 60 * 1000 },
    { label: "5 days ago", ms: 6 * 24 * 60 * 60 * 1000 },
    { label: "6 days ago", ms: 7 * 24 * 60 * 60 * 1000 },
    { label: "1 week ago", ms: 8 * 24 * 60 * 60 * 1000 },
    { label: "2 weeks ago", ms: 15 * 24 * 60 * 60 * 1000 },
    { label: "3 weeks ago", ms: 22 * 24 * 60 * 60 * 1000 },
    { label: "1 month ago", ms: 31 * 24 * 60 * 60 * 1000 },
    { label: "2 month ago", ms: 61 * 24 * 60 * 60 * 1000 },
    { label: "3 month ago", ms: 91 * 24 * 60 * 60 * 1000 },
  ] as const;

  return timeRanges.find((range) => timeDiff <= range.ms)?.label ?? "Older";
};
